import type { TutorQaAnswer } from '@/lib/deepseek'
import { followUpParts } from '@/lib/qa-follow-up'
import { TrText } from '@/components/tr-text'
import { SpeakButtons } from '@/components/speak-buttons'
import { hasTurkish } from '@/lib/tr-text'

// Строка "фраза + кнопки звука". Длинное турецкое слово (konuşmuyorsunuz) не
// давало фразе сжаться, и в "Крупном" микрофон вылезал за рамку карточки
// (задача 134): теперь кнопки при нехватке места уходят строкой ниже, вправо.
const ROW = { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', columnGap: 10, rowGap: 6 } as const
const ROW_TEXT = { flex: '1 1 9em', minWidth: 0, overflowWrap: 'break-word' } as const
const ROW_BUTTONS = { flexShrink: 0, marginLeft: 'auto' } as const

// Общая карточка ответа режима «Спросить» — используется и на отдельной
// вкладке (tutor-qa.tsx), и инлайн внутри диалога-практики (ai-chat.tsx),
// когда вопрос задан прямо посреди роleplay-разговора.
export function QaAnswerCard({ question, answer }: { question: string; answer: TutorQaAnswer }) {
  return (
    <div className="rounded-[16px] border p-4" style={{ borderColor: 'var(--tk-fire)' }}>
      {/* Вопрос ученика и сам ответ — крупные и белые по прямому запросу
          пользователя: до этого оба были мельче (13/15px) и тёплого оттенка,
          и читать их на насыщенном синем фоне панели было тяжело. Цветовая
          разметка осталась только на служебных блоках ниже (разбор ошибки,
          пример, мини-задание) — там цвет отличает их от ответа. */}
      <div className="mb-2 text-[16px] leading-snug font-semibold" style={{ color: 'var(--foreground)' }}>
        <TrText text={question} />
      </div>

      {answer.correction && (
        <div
          className="mb-2 rounded-[12px] border px-3 py-2 text-[16px]"
          style={{ borderColor: 'var(--tk-fire)', background: 'oklch(0.6 0.14 228 / 0.16)', color: 'var(--tk-translate)' }}
        >
          <div style={ROW}>
            <div className="font-semibold" style={ROW_TEXT}><TrText text={answer.correction.corrected} /></div>
            <span style={ROW_BUTTONS}><SpeakButtons text={answer.correction.corrected} record /></span>
          </div>
          <div className="mt-0.5 opacity-90">{answer.correction.explanationRu}</div>
        </div>
      )}

      {/* pre-line — переносы строк из ответа модели (пункты списка) не
          склеиваются в один абзац. */}
      <div className="mb-2.5 text-[17px] leading-[1.55] font-medium" style={{ color: 'var(--foreground)', whiteSpace: 'pre-line' }}>
        <TrText text={answer.answer} />
      </div>

      {/* Непрозрачный `tk-surface` вместо полупрозрачной синей заливки —
          на ярком фоне панели (см. tutor-fab.tsx) полупрозрачный тинт почти
          сливался с фоном и текст внутри было плохо видно. */}
      <div className="rounded-[12px] border p-3 text-[16px]" style={{ background: 'var(--tk-surface)', borderColor: 'var(--tk-border)' }}>
        {/* Турецкий пример — ярко-голубым и крупнее: прежний --tk-ice (тёмно-синий)
            на тёмной плашке почти не читался. */}
        <div style={ROW}>
          <div className="text-[16px] font-semibold" style={{ ...ROW_TEXT, color: 'oklch(0.86 0.11 225)' }}>
            <TrText text={answer.example.turkish} />
          </div>
          <span style={ROW_BUTTONS}><SpeakButtons text={answer.example.turkish} record /></span>
        </div>
        {answer.example.ru && (
          <div className="mt-0.5" style={{ color: 'var(--tk-translate)' }}>
            {answer.example.ru}
          </div>
        )}
        {answer.example.situation && (
          <div className="mt-1 text-[15px]" style={{ color: 'oklch(0.88 0.02 240)' }}>
            {answer.example.situation}
          </div>
        )}
      </div>

      {answer.followUp && (
        <div className="mt-2 text-[16px] font-medium" style={{ ...ROW, color: 'var(--tk-gold)' }}>
          <div style={ROW_TEXT}>
            {(() => {
              const f = followUpParts(answer.followUp)
              return (
                <>
                  {f.label && <>{f.label} </>}
                  <TrText text={f.text} />
                </>
              )
            })()}
          </div>
          {/* Задание бывает по-русски или вперемешку — турецкий голос читал бы кириллицу. */}
          {hasTurkish(answer.followUp) && !/[а-яё]/i.test(answer.followUp) && (
            <span style={ROW_BUTTONS}><SpeakButtons text={answer.followUp} withSlow={false} /></span>
          )}
        </div>
      )}
    </div>
  )
}
